import ReactDOM from "react-dom/client";
import axios from "axios";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

// Family isolation (2026-02): send the `frx_family_id` HttpOnly cookie
// on every backend call so each browser gets its own family scope.
axios.defaults.withCredentials = window.location.protocol !== "capacitor:";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

document.documentElement.classList.toggle(
  "frontrow-native",
  window.location.protocol === "capacitor:"
);

const root = ReactDOM.createRoot(document.getElementById("root"));
// Clerk's verification UI must mount exactly once. React StrictMode's
// development-only remount cycle can replay Clerk's verification transition
// and issue two different email codes from one parent action.
root.render(
  <QueryClientProvider client={queryClient}>
    <App />
  </QueryClientProvider>,
);

// Installed-PWA update contract:
// - bypass the HTTP cache when checking sw.js
// - check immediately after registration and whenever the app returns
//   to the foreground / pageshow / focus
// - when an already-controlled FrontRow app receives a genuinely new worker,
//   reload exactly once so the parent moves onto the fresh release without
//   signing out, reinstalling, or manually closing/reopening the app
// - do not reload on first-ever service-worker registration
if ("serviceWorker" in navigator && process.env.NODE_ENV === "production") {
  window.addEventListener("load", () => {
    const hadControllerAtLoad = Boolean(navigator.serviceWorker.controller);
    let reloadingForUpdate = false;

    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (!hadControllerAtLoad || reloadingForUpdate) return;
      // Never remount Clerk while an email-code sign-in/up flow is active.
      // The new worker already controls the page; the authenticated route can
      // pick up the fresh bundle on the next safe navigation or app launch.
      if (/^\/sign-(?:in|up)(?:\/|$)/.test(window.location.pathname)) return;
      reloadingForUpdate = true;
      window.location.reload();
    });

    navigator.serviceWorker
      .register("/sw.js", { updateViaCache: "none" })
      .then((reg) => {
        const checkForUpdate = () => reg.update().catch(() => {});

        // Do not wait for the next PWA launch to discover a deploy.
        checkForUpdate();

        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") checkForUpdate();
        });
        window.addEventListener("focus", checkForUpdate);
        window.addEventListener("pageshow", checkForUpdate);
      })
      .catch(() => {
        // Registration failure is non-fatal — FrontRow remains usable online.
      });
  });
}
